import React, { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  HomeIcon,
  CalendarIcon,
  UserGroupIcon,
  Bars3Icon,
  PlusIcon
} from '@heroicons/react/24/outline'
import {
  HomeIcon as HomeIconSolid,
  CalendarIcon as CalendarIconSolid,
  UserGroupIcon as UserGroupIconSolid
} from '@heroicons/react/24/solid'
import { triggerHaptic } from '../../hooks/useMobile'

interface NavItem {
  path: string
  label: string
  icon: React.ElementType
  iconActive: React.ElementType
}

const navItems: NavItem[] = [
  {
    path: '/dashboard',
    label: 'Home',
    icon: HomeIcon,
    iconActive: HomeIconSolid
  },
  {
    path: '/schedule',
    label: 'Schedule',
    icon: CalendarIcon,
    iconActive: CalendarIconSolid
  }
]

const rightNavItems: NavItem[] = [
  {
    path: '/clients',
    label: 'Clients',
    icon: UserGroupIcon,
    iconActive: UserGroupIconSolid
  }
]

interface BottomNavProps {
  onMenuOpen: () => void
  onFabClick?: () => void
}

export default function BottomNav({ onMenuOpen, onFabClick }: BottomNavProps) {
  const location = useLocation()
  const [fabPressed, setFabPressed] = useState(false)

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === path
    }
    return location.pathname.startsWith(path)
  }

  const handleFabClick = () => {
    triggerHaptic('medium')
    setFabPressed(true)
    setTimeout(() => setFabPressed(false), 200)
    if (onFabClick) {
      onFabClick()
    }
  }

  const styles = {
    nav: {
      position: 'fixed' as const,
      bottom: 0,
      left: 0,
      width: '100%',
      height: '72px',
      background: 'rgba(8, 10, 15, 0.95)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderTop: '1px solid rgba(255, 255, 255, 0.08)',
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      zIndex: 100
    },
    navItem: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      minWidth: '48px',
      minHeight: '48px',
      padding: '8px 12px',
      color: 'rgba(255, 255, 255, 0.5)',
      textDecoration: 'none',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      transition: 'color 0.2s ease, transform 0.15s ease',
      WebkitTapHighlightColor: 'transparent'
    },
    navItemActive: {
      color: 'var(--accent, #00f2ff)',
      position: 'relative' as const
    },
    navItemIndicator: {
      position: 'absolute' as const,
      top: '-4px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '20px',
      height: '2px',
      background: 'var(--accent, #00f2ff)',
      boxShadow: '0 2px 8px var(--accent, #00f2ff)',
      borderRadius: '0 0 2px 2px'
    },
    navIcon: {
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    navLabel: {
      fontSize: '12px',
      fontWeight: 500,
      letterSpacing: '0.2px',
      lineHeight: 1,
      whiteSpace: 'nowrap' as const
    },
    fabWrapper: {
      position: 'relative' as const,
      top: '-24px'
    },
    fab: {
      width: '56px',
      height: '56px',
      background: 'var(--accent, #00f2ff)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#080a0f',
      fontSize: '1.5rem',
      boxShadow: '0 0 20px rgba(var(--org-primary-rgb, 0, 242, 255), 0.4)',
      border: '4px solid #080a0f',
      cursor: 'pointer',
      transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease',
      WebkitTapHighlightColor: 'transparent'
    },
    fabPressed: {
      transform: 'scale(0.9)',
      boxShadow: '0 0 30px rgba(var(--org-primary-rgb, 0, 242, 255), 0.6)'
    },
    fabIcon: {
      width: '28px',
      height: '28px'
    }
  }

  return (
    <nav style={styles.nav} className="bottom-nav">
      {/* Left nav items */}
      {navItems.map((item) => {
        const active = isActive(item.path)
        const Icon = active ? item.iconActive : item.icon

        return (
          <NavLink
            key={item.path}
            to={item.path}
            style={{
              ...styles.navItem,
              ...(active ? styles.navItemActive : {})
            }}
            onClick={() => triggerHaptic('light')}
          >
            {active && <div style={styles.navItemIndicator} />}
            <div style={styles.navIcon}>
              <Icon style={{ width: '24px', height: '24px' }} />
            </div>
            <span style={styles.navLabel}>{item.label}</span>
          </NavLink>
        )
      })}

      {/* Center FAB */}
      <div style={styles.fabWrapper}>
        <button
          type="button"
          style={{
            ...styles.fab,
            ...(fabPressed ? styles.fabPressed : {})
          }}
          onClick={handleFabClick}
          onTouchStart={() => setFabPressed(true)}
          onTouchEnd={() => {
            setFabPressed(false)
            handleFabClick()
          }}
          aria-label="Quick action"
        >
          <PlusIcon style={styles.fabIcon} />
        </button>
      </div>

      {/* Right nav items */}
      {rightNavItems.map((item) => {
        const active = isActive(item.path)
        const Icon = active ? item.iconActive : item.icon

        return (
          <NavLink
            key={item.path}
            to={item.path}
            style={{
              ...styles.navItem,
              ...(active ? styles.navItemActive : {})
            }}
            onClick={() => triggerHaptic('light')}
          >
            {active && <div style={styles.navItemIndicator} />}
            <div style={styles.navIcon}>
              <Icon style={{ width: '24px', height: '24px' }} />
            </div>
            <span style={styles.navLabel}>{item.label}</span>
          </NavLink>
        )
      })}

      {/* Menu button */}
      <button
        type="button"
        style={styles.navItem}
        onClick={() => {
          triggerHaptic('light')
          onMenuOpen()
        }}
        onTouchEnd={(e) => {
          e.preventDefault()
          triggerHaptic('light')
          onMenuOpen()
        }}
        aria-label="Open menu"
      >
        <div style={styles.navIcon}>
          <Bars3Icon style={{ width: '24px', height: '24px' }} />
        </div>
        <span style={styles.navLabel}>Menu</span>
      </button>
    </nav>
  )
}
